import { Component, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorBase } from '@shared/bases/control-value-accessor.base';
import { SharedModule } from '@shared/modules/shared.module';
import { MultiSelect } from 'primeng/multiselect';
import { AvatarComponent } from '../avatar/avatar.component';
import { ChipComponent } from '../chip/chip.component';

@Component({
	selector: 'app-multiselect',
	imports: [SharedModule, ChipComponent, AvatarComponent],
	templateUrl: './multiselect.component.html',
	styleUrl: './multiselect.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => MultiselectComponent),
			multi: true,
		},
	],
})
export class MultiselectComponent extends ControlValueAccessorBase<any[]> {
	private readonly MAX_LABEL_LENGTH = 40;
	private _options: {
		label: string;
		value: any;
		icon?: string;
		iconType?: 'image' | 'lucide';
		employeeId?: number;
		profilePhoto?: string | null;
	}[] = [];

	@Input() size: 'xsmall' | 'small' | 'medium' | 'large' = 'large';
	// Optional so the default comes from i18n, like app-select. Hard-coded
	// English defaults leaked "Choose multiple options" / "This field is
	// required" / "Search..." into Spanish forms, the public onboarding
	// among them.
	@Input() placeholder?: string;
	@Input() disabled = false;
	@Input() error = false;
	@Input() errorMessage?: string;
	@Input() label?: string;
	@Input() showIcon = false;
	@Input() iconType: 'image' | 'lucide' = 'image'; // Type of icon to render
	@Input() filter = false; // Enable search/filter functionality
	@Input() filterPlaceholder?: string; // Search placeholder
	// Label shown in the box when more than maxSelectedLabels are selected ("{0}" = count).
	// Falls back to a translated default when not provided.
	@Input() selectedItemsLabel?: string;
	@Input() showSingleLabel = true; // Show single item label instead of "1 item selected"
	@Input() displayMode: 'chip' | 'list' = 'chip'; // Display mode: chip (tags) or list (vertical list)
	@Input() showAvatar = false; // Show avatar instead of icon (requires employeeId in options)
	@Input() appendTo: any = null; // Where to append the overlay panel

	@Output() onOpen = new EventEmitter<void>();

	@ViewChild('multiselectElement') multiselectComponent?: MultiSelect;

	@Input()
	set options(value: { label: string; value: any }[]) {
		if (!value || !Array.isArray(value)) {
			this._options = [];
			return;
		}
		this._options = value.map((option) => ({
			...option,
			label: this.truncateText(option.label || '', this.MAX_LABEL_LENGTH),
		}));
	}

	get options(): { label: string; value: any }[] {
		return this._options;
	}

	multiselectId = `multiselect-${Math.random().toString(36).substring(2, 11)}`;
	isDropdownOpen = false;
	private _selectedOptions: any[] = [];

	set selectedOptions(value: any[]) {
		const newValue = value || [];
		// Only update if the value actually changed to prevent infinite loops
		if (JSON.stringify(this._selectedOptions) !== JSON.stringify(newValue)) {
			this._selectedOptions = newValue;
			this.value = newValue;
			this.notifyValueChange(newValue);
		}
	}

	get selectedOptions(): any[] {
		return this._selectedOptions;
	}

	override writeValue(value: any[]): void {
		this._selectedOptions = value || [];
		super.writeValue(this._selectedOptions);
	}

	onDropdownOpen(): void {
		this.isDropdownOpen = true;
		this.onOpen.emit();
	}

	onDropdownClose(): void {
		this.isDropdownOpen = false;
	}

	closeDropdown(): void {
		if (this.multiselectComponent && this.isDropdownOpen) {
			this.multiselectComponent.hide();
		}
	}

	// Get the label for single selected item
	getSingleSelectedLabel(): string {
		if (!this.showSingleLabel || !this.selectedOptions || this.selectedOptions.length !== 1) {
			return '';
		}
		const selectedOption = this._options.find((opt) => opt.value === this.selectedOptions[0]);
		return selectedOption?.label || '';
	}

	private truncateText(text: string, maxLength: number): string {
		return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
	}

	// Get label for a selected value
	getOptionLabel(value: any): string {
		const option = this._options.find((opt) => opt.value === value);
		return option?.label || '';
	}

	// Get icon for a selected value
	getOptionIcon(value: any): string | undefined {
		const option = this._options.find((opt) => opt.value === value);
		return option?.icon;
	}

	// Get icon type for a selected value
	getOptionIconType(value: any): 'image' | 'lucide' {
		const option = this._options.find((opt) => opt.value === value);
		return option?.iconType || this.iconType;
	}

	// Get employeeId for a selected value
	getOptionEmployeeId(value: any): number | undefined {
		const option = this._options.find((opt) => opt.value === value);
		return option?.employeeId;
	}

	// Remove an item from selection
	removeItem(value: any): void {
		if (this.disabled) return;
		this.selectedOptions = this.selectedOptions.filter((v) => v !== value);
	}
}
